<template>

    <div>
        <div v-if="loading" class="text-center my-2">
            <b-spinner class="align-middle" />
            <div>
                <strong>Loading...</strong>
            </div>

        </div>
        <div v-else-if="loadError && !accessDenied" class="text-center p-4">
            <strong>There was an error loading PAR Level Items.</strong>
        </div>
        <div v-else-if="accessDenied" class="text-center p-4">
            <div class="alert alert-info">
                <i class="fa fa-info-circle"></i>
                <strong>PAR Level Information:</strong>
                <p>You don't have permission to view inventory PAR levels for this item.</p>
            </div>
        </div>
        <b-table v-if="!loading && !loadError && !accessDenied" :items="filteredItems" :fields="fields" :sort-by.sync="sortBy" :sort-desc.sync="sortDesc"
            :tbody-tr-class="rowClass" show-empty empty-text="No PAR Level entries to display.">

            <template #cell(inAlert)="{ value }">
                <i v-if="value" class="fa fa-fw fa-warning" />
            </template>

            <template #cell(includeEquivalents)="{ value }">
                <i v-if="value" class="fa fa-fw fa-check text-success" />
            </template>

            <template #cell(actions)="data">
                <div>
                    <b-button variant="primary" title="Edit" size="sm" @click="edit(data.item)">
                        <b-icon icon="pencil" />
                    </b-button>
                    <b-button variant="danger" title="Delete" size="sm" @click="deleteItem(data.item)">
                        <b-icon icon="trash" />
                    </b-button>
                </div>
            </template>

            <template #cell(unitsOnHand)="data">
                {{ data.item.unitsOnHand }}
                <div v-if="data.item.unitsOnHand">
                    <b-badge :variant="data.item.inAlert ? 'danger' : 'primary'">
                        Current: {{ data.item.currentInventoryQuantity }}
                    </b-badge>
                </div>
            </template>

            <template #cell(daysOnHand)="data">
                {{ data.item.daysOnHand }}
                <div v-if="data.item.daysOnHand">
                    <b-badge :variant="data.item.inAlert ? 'danger' : 'primary'">
                        Current: {{ data.item.currentDaysOnHand }}
                    </b-badge>
                </div>
            </template>

        </b-table>

        <div v-if="!accessDenied" class="text-center">
            <b-button variant="success" @click="add">
                Add PAR Level
            </b-button>
        </div>

        <b-modal id="par-level-modal" title="Inventory PAR Level" no-fade centered @ok="saveItem">
            <template v-if="selectedItem">
                <b-form-group v-if="selectedItem.id" label="Drug:">
                    <b-input :value="selectedItem.drugName" readonly />
                </b-form-group>
                <SearchComponent v-else :id="selectedDrug?.drugId" v-model="selectedDrug" :label="`Drug`"
                    :search-u-r-l="`/Drug`" required :disabled="drug"
                    :params="{ onlyActive: false, allSources: true }" />

                <SearchComponent v-model="selectedStore" label="Store" search-u-r-l="/store" prepopulate required />
                <SearchComponent v-model="selectedProgram" label="Program" search-u-r-l="/Program" prepopulate
                    clearable />
                <SearchComponent v-if="hasInventoryLocations" :id="selectedLocation?.id" v-model="selectedLocation"
                    search-u-r-l="/inventory/inventory-locations" prepopulate label="Location"
                    :results-modifier="addLocationOptions" />
                <b-row>
                    <b-col>
                        <b-form-group label="Units on Hand">
                            <b-form-input v-model="selectedItem.unitsOnHand" type="number" />
                        </b-form-group>
                    </b-col>
                    <b-col>
                        <b-form-group label="Days on Hand">
                            <b-form-input v-model="selectedItem.daysOnHand" type="number" />
                        </b-form-group>
                    </b-col>
                </b-row>
                <b-form-checkbox v-model="selectedItem.includeEquivalents">
                    Include Equivalents
                    <HelpPopup text="When enabled, therapeutically equivalent drugs are included when calculating current inventory levels for PAR alerts." />
                </b-form-checkbox>
            </template>
        </b-modal>
    </div>
</template>

<script lang="ts">
import { InventoryParLevelViewModel } from "@/models/InventoryParLevel";
import { Component, Vue, Prop } from "vue-property-decorator";
import SearchComponent from '@/components/SearchComponent.vue';
import HelpPopup from '@/components/HelpPopup.vue';
import { Drug } from "@/models/Drug/Drug";
import { Store } from "@/models/Store";
import { Program } from "@/models/Program";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import { toRaw } from "vue";
import { InventoryLocation } from "@/models/InventoryLocation";

const anyLocation = Object.assign(new InventoryLocation(), { id: null, name: "Any Location" });

@Component({
    name: "PeriodicInventoryDetailsPage",
    components: {
        SearchComponent,
        HelpPopup
    }
})
export default class InventoryParLevelsComponent extends Vue {

    @Prop() protected drug?: Drug;

    private items: InventoryParLevelViewModel[] = [];
    private sortBy = "inAlert";
    private sortDesc = true;
    private selectedItem: InventoryParLevelViewModel | null = null;
    private selectedDrug: Drug = new Drug();
    private selectedStore: Store = new Store();
    private selectedProgram: Program = new Program();
    private selectedLocation: InventoryLocation = new InventoryLocation();
    private loading = true;
    private loadError = false;
    private accessDenied = false;

    mounted() {
        this.loadHasInventoryLocations();
        this.loadItems();
    }

    get fields() {
        return [
            { key: "inAlert", sortable: true, class: "in-alert-col", label: "Alert" },
            { key: "drugName" },
            { key: "storeName" },
            { key: "programName" },
            { key: "locationName", hidden: !this.hasInventoryLocations },
            { key: "unitsOnHand" },
            { key: "daysOnHand" },
            { key: "includeEquivalents", label: "Equivalents" },
            { key: "actions", label: "", class: "actions-col" }
        ].filter(f => f.hidden !== true)
    }

    rowClass(item: InventoryParLevelViewModel, type: string) {
        if (!item || type !== 'row') return
        if (item.inAlert) return 'table-danger in-alert-row'
    }

    private add() {
        this.selectedItem = {
            id: 0,
            includeEquivalents: false,
            created: new Date(),
            createdBy: "",
            updated: new Date(),
            updatedBy: ""
        };
        this.selectedDrug = this.drug ?? new Drug();
        this.selectedStore = new Store();
        this.selectedProgram = new Program();
        this.selectedLocation = anyLocation;

        this.$bvModal.show("par-level-modal");
    }

    private edit(item: InventoryParLevelViewModel) {
        this.selectedItem = structuredClone(toRaw(item));
        this.selectedDrug = new Drug(item.drugId);
        this.selectedStore = Object.assign(new Store(), { id: item.storeId });
        this.selectedProgram = Object.assign(new Program(), { id: item.programId });
        this.selectedLocation = Object.assign(new InventoryLocation(), { id: item.locationId, name: item.locationName });
        this.$bvModal.show("par-level-modal");
    }

    async loadItems() {
        try {
            this.loadError = false;
            this.accessDenied = false;
            this.loading = true;
            const response = await this.$http.get<InventoryParLevelViewModel[]>("/inventory/par-levels");

            this.items = response.data;
        } catch (err: any) {
            if (err.response?.status === 403) {
                this.accessDenied = true;
            } else {
                this.loadError = true;
                this.$notification(NotificationOptions.error(err));
            }
        } finally {
            this.loading = false;
        }
    }

    get filteredItems() {
        let items = this.items;
        if (this.drug) {
            items = items.filter(i => i.drugSource === this.drug?.source && i.drugId === this.drug?.drugId);
        }
        return items;
    }

    async deleteItem(item: InventoryParLevelViewModel) {
        try {
            const confirm = await this.$bvModal.msgBoxConfirm("Are you sure you want to delete this Inventory PAR Level?",
                {
                    title: "Delete Inventory PAR Level",
                    noFade: true,
                    centered: true,
                    okTitle: "Delete",
                    okVariant: "danger"
                });

            if (!confirm) return;

            await this.$http.delete("/inventory/par-level/" + item.id);
            this.loadItems();
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    async saveItem(e?: any) {
        e?.preventDefault();

        try {

            const errorMsgOptions = { title: "Error", centered: true, noFade: true };

            if (!this.selectedDrug?.drugId) {
                this.$bvModal.msgBoxOk("You must select a drug.", errorMsgOptions);
                return;
            }

            if (!this.selectedStore?.id) {
                this.$bvModal.msgBoxOk("You must select a store.", errorMsgOptions);
                return;
            }

            if (!this.selectedItem!.unitsOnHand && !this.selectedItem!.daysOnHand) {
                this.$bvModal.msgBoxOk("You must enter a value for Units on Hand or Days on Hand.", errorMsgOptions);
                return;
            }

            if (!this.selectedItem!.id) {
                this.selectedItem!.drugSource = this.drug?.source ?? this.selectedDrug.source;
                this.selectedItem!.drugId = this.drug?.id ?? this.selectedDrug.id;
            }

            this.selectedItem!.storeId = this.selectedStore.id as number;
            this.selectedItem!.programId = this.selectedProgram?.id;
            this.selectedItem!.unitsOnHand = !this.selectedItem!.unitsOnHand ? null : this.selectedItem!.unitsOnHand;
            this.selectedItem!.daysOnHand = !this.selectedItem!.daysOnHand ? null : this.selectedItem!.daysOnHand;
            this.selectedItem!.locationId = this.selectedLocation.id;

            await this.$http.post("/inventory/par-level", this.selectedItem);

            this.loadItems();

            this.$bvModal.hide("par-level-modal");
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    addLocationOptions(locations: Array<InventoryLocation>, params: { searchTerm: string }) {
        if (params.searchTerm?.trim()) return locations;
        return [
            anyLocation,
            Object.assign(new InventoryLocation(), { id: 0, name: "No Location Specified" }),
            { heading: true, label: "Locations" },
            ...locations
        ];
    }

    protected hasInventoryLocations = false;

    async loadHasInventoryLocations() {
        try {
            this.hasInventoryLocations = (await this.$http.get<boolean>("/inventory/has-inventory-locations")).data;
        } catch {
            //ignore
        }
    }

}

</script>

<style scoped lang="scss">
::v-deep(.in-alert-col) {
    width: 50px;
    text-align: center;
}

::v-deep(.in-alert-row) {
    font-weight: bold;
}

::v-deep(.actions-col) {
    width: 110px;

    >div {
        display: flex;
        gap: 5px;
    }

}
</style>
